import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { LogoLoader } from '@/components/ui/logo-loader';

import { CredentialNotificationRetry } from './credential-notification-retry';
import { CredentialRequestOverlay } from './credential-request-overlay';
import {
  type SavedCredentialRequest as Request,
  useSavedCredentialActions,
} from './saved-credential.query';

export const SavedCredentialRequest = ({
  request,
  onBeforeSubmit,
  overlay = false,
}: {
  request: Request;
  onBeforeSubmit?: () => void | Promise<void>;
  overlay?: boolean;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const actions = useSavedCredentialActions(request);
  const pending = request.status === 'pending';
  const showChoices =
    pending && (!submitted || actions.isError) && !actions.isPending;

  const send = async (
    body:
      | { action: 'approve' | 'reject' }
      | { action: 'choose'; section_id: string; choice_id: string }
  ) => {
    setSubmitted(true);
    await actions.submit(body, onBeforeSubmit);
  };

  if (overlay && request.status === 'resolved' && !request.notified) {
    return (
      <CredentialNotificationRetry
        pending={actions.isPending}
        isError={actions.isError}
        onRetry={() => void actions.resume()}
      />
    );
  }
  if (overlay && !showChoices) return null;

  const content = (
    <YStack
      className="min-w-0 overflow-hidden rounded-xl border border-border-strong bg-surface text-foreground"
      data-testid="saved-credential-request"
    >
      {showChoices ? (
        request.sections.length ? (
          <>
            {request.sections.map((section) => (
              <YStack
                key={section.id}
                role="group"
                aria-label={section.label}
                className="border-b border-border pb-2"
              >
                <h2 className="border-b border-border px-4 py-4 text-base leading-snug font-medium break-words">
                  {section.label}
                </h2>
                {section.choices.map((choice, index) => (
                  <Button
                    key={choice.id}
                    variant="ghost"
                    className="mx-2 h-auto min-h-11 rounded-lg px-2 py-3 [&>span]:w-full [&>span]:justify-start [&>span]:break-words [&>span]:text-left [&>span]:whitespace-normal"
                    onClick={() =>
                      void send({
                        action: 'choose',
                        section_id: section.id,
                        choice_id: choice.id,
                      })
                    }
                  >
                    <span
                      aria-hidden="true"
                      className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-surface-hover text-sm text-foreground-muted"
                    >
                      {index + 1}
                    </span>
                    {choice.label}
                  </Button>
                ))}
              </YStack>
            ))}
            <Button
              variant="ghost"
              className="m-3 min-h-11 self-end"
              onClick={() => void send({ action: 'reject' })}
            >
              Reject
            </Button>
          </>
        ) : (
          <>
            <YStack className="gap-1 border-b border-border px-4 py-4">
              <h2 className="text-base font-medium">Use saved sign-in?</h2>
              <p className="break-all text-sm leading-relaxed text-foreground-muted">
                {request.credential_id}
              </p>
            </YStack>
            <XStack className="justify-end gap-3 px-4 py-3">
              <Button
                variant="secondary"
                className="min-h-11 flex-1 sm:flex-none"
                onClick={() => void send({ action: 'reject' })}
              >
                Reject
              </Button>
              <Button
                className="min-h-11 flex-1 sm:flex-none"
                onClick={() => void send({ action: 'approve' })}
              >
                Use sign-in
              </Button>
            </XStack>
          </>
        )
      ) : (
        <YStack center className="min-h-40 gap-3 px-4 py-8 text-center">
          {request.status !== 'expired' ? (
            <YStack aria-hidden="true" className="size-8">
              <LogoLoader activity="think" flightOnly />
            </YStack>
          ) : null}
          <p
            role="status"
            className="text-sm leading-relaxed text-foreground-muted"
          >
            {request.status === 'expired'
              ? 'Request expired.'
              : request.status === 'resolved' && !request.notified
                ? 'The agent hasn’t received your response yet.'
                : actions.isPending || (submitted && pending)
                  ? 'Processing your response…'
                  : 'Working on the next step…'}
          </p>
        </YStack>
      )}
      {actions.isError ? (
        <p
          role="alert"
          className="px-4 pb-4 text-sm leading-relaxed text-danger-11"
        >
          Request failed. Please try again.
        </p>
      ) : null}
      {request.status === 'resolved' && !request.notified ? (
        <Button
          className="m-4 mt-0 min-h-11"
          disabled={actions.isPending}
          onClick={() => void actions.resume()}
        >
          Retry
        </Button>
      ) : null}
    </YStack>
  );

  return overlay ? (
    <CredentialRequestOverlay label="Saved credential request">
      {content}
    </CredentialRequestOverlay>
  ) : (
    content
  );
};
